/**
 * CLK Predictive Search — SKU-first + MX13 access filter
 * Intercepts Empire live-search /search/suggest fetches without editing empire.js.
 */
(function () {
  if (window.__clkPredictiveSearchInstalled) return;
  window.__clkPredictiveSearchInstalled = true;

  const originalFetch = window.fetch.bind(window);
  let debounceTimer = null;
  let searchGeneration = 0;
  const skuCache = {};
  let missingContainerLogged = false;

  const RESTRICTED_PRODUCT_TAG = 'GMSMX13';
  const REQUIRED_CUSTOMER_TAG = 'MX13';
  const RESTRICTED_COLLECTION_HANDLES = ['gms-mx13-restricted-key-system'];
  const RESTRICTED_COLLECTION_TITLES_NORMALIZED = ['gms mx13 restricted key system'];
  const RESULTS_SELECTOR = '.search-flydown--results, [data-live-search-results]';

  const emptyPredictiveHTML =
    '<div id="shopify-section-predictive-search" class="shopify-section"><div></div></div>';

  function customerMayViewRestrictedProducts() {
    const ctx = window.__predictiveSearchCustomer;
    if (!ctx || !ctx.loggedIn || !Array.isArray(ctx.tags)) return false;
    return ctx.tags.some((t) => String(t).trim() === REQUIRED_CUSTOMER_TAG);
  }

  function normalizeProductTagList(tags) {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags.map((t) => String(t).trim()).filter(Boolean);
    if (typeof tags === 'string') {
      return tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
    }
    return [];
  }

  function productHasRestrictedTag(tags) {
    const want = RESTRICTED_PRODUCT_TAG.toLowerCase();
    return normalizeProductTagList(tags).some((t) => t.toLowerCase() === want);
  }

  function filterProductsByCustomerAccess(products) {
    const allowed = customerMayViewRestrictedProducts();
    return products.filter((p) => {
      if (!productHasRestrictedTag(p._productTagsCsv)) return true;
      return allowed;
    });
  }

  function collectionHandleFromSuggest(c) {
    if (c && typeof c.handle === 'string' && c.handle.trim()) {
      return c.handle.trim().toLowerCase();
    }
    const url = c && c.url ? String(c.url) : '';
    try {
      const u = new URL(url, window.location.origin);
      const m = u.pathname.match(/\/collections\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]).toLowerCase() : '';
    } catch (e) {
      return '';
    }
  }

  function normalizeCollectionTitleForMatch(title) {
    return String(title || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function collectionIsMx13Restricted(c) {
    const h = collectionHandleFromSuggest(c);
    if (h && RESTRICTED_COLLECTION_HANDLES.indexOf(h) !== -1) return true;
    const t = normalizeCollectionTitleForMatch(c && c.title);
    if (t && RESTRICTED_COLLECTION_TITLES_NORMALIZED.indexOf(t) !== -1) return true;
    return false;
  }

  function filterCollectionsByCustomerAccess(collections) {
    const allowed = customerMayViewRestrictedProducts();
    return collections.filter((c) => {
      if (!collectionIsMx13Restricted(c)) return true;
      return allowed;
    });
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function cleanSearchTerm(raw) {
    let term = String(raw || '').trim();
    // Empire may prepend "tag:… AND " when a live-search filter is active
    const andIdx = term.toUpperCase().lastIndexOf(' AND ');
    if (andIdx !== -1) term = term.slice(andIdx + 5).trim();
    return term;
  }

  function formatMoney(amount, fromCents) {
    let value = Number(amount);
    if (!Number.isFinite(value)) value = 0;

    // product.js prices are cents; suggest.json is often already dollars
    const cents = fromCents ? Math.round(value) : Math.round(value * 100);

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const fmt =
        window.__predictiveSearchMoneyFormat ||
        window.Shopify.money_format ||
        '${{amount}}';
      try {
        return window.Shopify.formatMoney(cents, fmt);
      } catch (e) {
        /* fall through */
      }
    }

    return '$' + (cents / 100).toFixed(2);
  }

  function productImageUrl(product) {
    const img = product.featured_image;
    if (!img) return '';
    if (typeof img === 'string') return img;
    if (img.url) return img.url;
    if (img.src) return img.src;
    return '';
  }

  function getResultsContainer() {
    const el = document.querySelector(RESULTS_SELECTOR);
    if (!el && !missingContainerLogged) {
      missingContainerLogged = true;
      console.warn(
        '[CLK predictive search] Results container not found. Expected:',
        RESULTS_SELECTOR
      );
    }
    return el;
  }

  async function fetchProductDetails(handle) {
    if (!handle) return { sku: '', variants: [], tags: '', priceCents: null };
    if (skuCache[handle]) return skuCache[handle];
    try {
      const res = await originalFetch('/products/' + encodeURIComponent(handle) + '.js');
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      const result = {
        sku: (data && data.variants && data.variants[0] && data.variants[0].sku) || '',
        variants: (data && data.variants) || [],
        tags: data && data.tags != null ? data.tags : '',
        priceCents:
          data && data.price != null
            ? Number(data.price)
            : data && data.variants && data.variants[0]
              ? Number(data.variants[0].price)
              : null,
      };
      skuCache[handle] = result;
      return result;
    } catch (e) {
      return { sku: '', variants: [], tags: '', priceCents: null };
    }
  }

  async function fetchProducts(searchTerm) {
    const q = encodeURIComponent(searchTerm);
    const [skuRes, kwRes] = await Promise.all([
      originalFetch(
        '/search/suggest.json?q=' +
          q +
          '&resources[type]=product&resources[limit]=5&resources[options][fields]=variants.sku'
      ),
      originalFetch(
        '/search/suggest.json?q=' +
          q +
          '&resources[type]=product&resources[limit]=8&resources[options][fields]=title,product_type,variants.title,vendor,tag'
      ),
    ]);

    const [skuData, kwData] = await Promise.all([skuRes.json(), kwRes.json()]);
    const skuProducts =
      (skuData &&
        skuData.resources &&
        skuData.resources.results &&
        skuData.resources.results.products) ||
      [];
    const kwProducts =
      (kwData &&
        kwData.resources &&
        kwData.resources.results &&
        kwData.resources.results.products) ||
      [];

    const seen = new Set();
    const merged = [];
    for (const p of [...skuProducts, ...kwProducts]) {
      if (!p || p.id == null) continue;
      if (!seen.has(p.id)) {
        seen.add(p.id);
        merged.push(p);
      }
    }

    const details = await Promise.all(merged.map((p) => fetchProductDetails(p.handle)));
    merged.forEach((p, i) => {
      p.sku = details[i].sku;
      p.allVariants = details[i].variants;
      p._priceCents = details[i].priceCents;
      const fromJs = details[i].tags;
      const fromSuggest = p.tags != null ? p.tags : p.tag;
      p._productTagsCsv = normalizeProductTagList(fromJs).concat(
        normalizeProductTagList(fromSuggest)
      );
    });

    const term = searchTerm.toLowerCase();
    merged.sort((a, b) => {
      const aSkuMatch = a.sku && String(a.sku).toLowerCase().includes(term) ? 0 : 1;
      const bSkuMatch = b.sku && String(b.sku).toLowerCase().includes(term) ? 0 : 1;
      return aSkuMatch - bSkuMatch;
    });

    return filterProductsByCustomerAccess(merged);
  }

  async function fetchExtra(searchTerm) {
    const res = await originalFetch(
      '/search/suggest.json?q=' +
        encodeURIComponent(searchTerm) +
        '&resources[type]=collection,page,article&resources[limit]=5'
    );
    const data = await res.json();
    const results = (data && data.resources && data.resources.results) || {};
    return {
      collections: results.collections || [],
      pages: results.pages || [],
      articles: results.articles || [],
    };
  }

  function ensureFlydownVisible(flydown) {
    if (!flydown) return;
    flydown.classList.add('visible');
    flydown.setAttribute('data-animation-state', 'visible');

    const wrap = flydown.closest('[data-live-search-flydown]');
    if (wrap) {
      wrap.classList.add('visible');
      wrap.setAttribute('data-animation-state', 'open');
      wrap.style.setProperty('--open-height', 'auto');
    }

    const root = flydown.closest('[data-live-search]');
    if (root) root.classList.add('live-search--active', 'live-search--focused');

    const placeholder = root && root.querySelector('[data-live-search-placeholder]');
    if (placeholder) placeholder.classList.remove('visible');

    const quickLinks = root && root.querySelector('[data-live-search-quick-links]');
    if (quickLinks) quickLinks.classList.remove('visible');
  }

  function renderAll(products, collections, pages, articles) {
    const flydown = getResultsContainer();
    if (!flydown) return;

    flydown.innerHTML = '';
    const blogPages = [...pages, ...articles];
    const hasProducts = products.length > 0;
    const hasSide = collections.length > 0 || blogPages.length > 0;

    if (!hasProducts && !hasSide) {
      flydown.innerHTML =
        '<p class="search-flydown__no-results" data-live-search-no-products>No results found</p>';
      ensureFlydownVisible(flydown);
      return;
    }

    // Inner row keeps sidebar left + products right; View all stays full-width below
    const inner = document.createElement('div');
    inner.className = 'search-flydown--results-inner';

    if (hasSide) {
      const wrapper = document.createElement('div');
      wrapper.className = 'search-flydown__queries-collections-pages';

      if (collections.length > 0) {
        const section = document.createElement('div');
        section.className = 'search-flydown__queries-collections';
        section.innerHTML =
          '<h3 class="search-flydown__group-title">Collections</h3>' +
          '<ul class="search-flydown__group-list">' +
          collections
            .map(
              (c) =>
                '<li class="search-flydown__list-item"><a href="' +
                escapeHtml(c.url) +
                '">' +
                escapeHtml(c.title) +
                '</a></li>'
            )
            .join('') +
          '</ul>';
        wrapper.appendChild(section);
      }

      if (blogPages.length > 0) {
        const section = document.createElement('div');
        section.className = 'search-flydown__pages-posts-items';
        section.innerHTML =
          '<h5 class="search-flydown__group-title">Blog &amp; Pages</h5>' +
          '<ul class="search-flydown__group-list search-flydown__pages-posts-items-list">' +
          blogPages
            .map(
              (item) =>
                '<li class="search-flydown__list-item"><a href="' +
                escapeHtml(item.url) +
                '">' +
                escapeHtml(item.title) +
                '</a></li>'
            )
            .join('') +
          '</ul>';
        wrapper.appendChild(section);
      }

      inner.appendChild(wrapper);
    }

    if (hasProducts) {
      const productItems = document.createElement('div');
      productItems.className = 'search-flydown--product-items';
      const productListHTML = products
        .map((product) => {
          const image = productImageUrl(product);
          const sku = product.sku || '';
          let priceHtml;
          if (product._priceCents != null && Number.isFinite(Number(product._priceCents))) {
            priceHtml = formatMoney(product._priceCents, true);
          } else {
            const raw = Number(product.price || 0);
            priceHtml = formatMoney(raw, raw > 999);
          }
          return (
            '<li>' +
            '<a class="search-flydown--product" href="' +
            escapeHtml(product.url) +
            '">' +
            (image
              ? '<figure class="search-flydown--product-image"><img src="' +
                escapeHtml(image) +
                '" alt="' +
                escapeHtml(product.title) +
                '" width="70" height="70" loading="lazy"></figure>'
              : '') +
            '<div class="search-flydown--product-text">' +
            '<span class="search-flydown--product-title">' +
            escapeHtml(product.title) +
            '</span>' +
            (sku
              ? '<p class="search-flydown--product__sku">SKU: ' + escapeHtml(sku) + '</p>'
              : '') +
            '<div class="price search-flydown--product__price"><div class="price__current"><span class="money">' +
            escapeHtml(priceHtml) +
            '</span></div></div>' +
            '</div></a></li>'
          );
        })
        .join('');

      productItems.innerHTML =
        '<h3 class="search-flydown__group-title">Products</h3>' +
        '<ul class="search-flydown__group-list">' +
        productListHTML +
        '</ul>';
      inner.appendChild(productItems);
    }

    flydown.appendChild(inner);

    const form = flydown.closest('form') || document.querySelector('[data-live-search-form]');
    if (form) {
      const viewAll = document.createElement('div');
      viewAll.className = 'search-flydown__continue-wrap';
      viewAll.innerHTML =
        '<button type="submit" class="search-flydown__continue" data-live-search-view-all>View all results</button>';
      flydown.appendChild(viewAll);
    }

    ensureFlydownVisible(flydown);
  }

  async function runSearch(rawTerm, generation) {
    const searchTerm = cleanSearchTerm(rawTerm);
    if (!searchTerm) return;

    try {
      const [products, extra] = await Promise.all([
        fetchProducts(searchTerm),
        fetchExtra(searchTerm),
      ]);

      if (generation !== searchGeneration) return;

      renderAll(
        products,
        filterCollectionsByCustomerAccess(extra.collections),
        extra.pages,
        extra.articles
      );
    } catch (err) {
      if (generation !== searchGeneration) return;
      console.warn('[CLK predictive search] search failed', err);
      const flydown = getResultsContainer();
      if (flydown) {
        flydown.innerHTML =
          '<p class="search-flydown__no-results" data-live-search-no-products>No results found</p>';
        ensureFlydownVisible(flydown);
      }
    }
  }

  function scheduleSearch(rawTerm) {
    const searchTerm = cleanSearchTerm(rawTerm);
    if (!searchTerm) return;

    if (debounceTimer) clearTimeout(debounceTimer);
    const generation = ++searchGeneration;

    // Empire already debounces keyup ~250ms; keep a short settle delay only
    debounceTimer = setTimeout(function () {
      runSearch(searchTerm, generation);
    }, 50);
  }

  function urlLooksLikePredictiveSuggest(urlString) {
    if (!urlString) return false;
    // Match Empire section HTML suggest and any /search/suggest path
    if (urlString.indexOf('/search/suggest') !== -1) return true;
    try {
      const predictive = window.Theme && window.Theme.routes && window.Theme.routes.predictive_search_url;
      if (predictive && urlString.indexOf(predictive) !== -1) return true;
    } catch (e) {
      /* ignore */
    }
    return false;
  }

  function extractUrlString(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    try {
      return String(input);
    } catch (e) {
      return '';
    }
  }

  window.fetch = function (input, init) {
    const urlString = extractUrlString(input);

    // Only intercept HTML section predictive search — never block our own .json calls
    // (those go through originalFetch). Empire requests section_id=predictive-search.
    const isSectionSuggest =
      urlLooksLikePredictiveSuggest(urlString) &&
      urlString.indexOf('suggest.json') === -1 &&
      (urlString.indexOf('section_id=predictive-search') !== -1 ||
        urlString.indexOf('/search/suggest') !== -1);

    if (isSectionSuggest) {
      let searchTerm = '';
      try {
        const urlObj = new URL(urlString, window.location.origin);
        searchTerm = urlObj.searchParams.get('q') || '';
      } catch (e) {
        searchTerm = '';
      }

      scheduleSearch(searchTerm);

      return Promise.resolve(
        new Response(emptyPredictiveHTML, {
          status: 200,
          headers: { 'Content-Type': 'text/html' },
        })
      );
    }

    return originalFetch(input, init);
  };
})();
